import {
  useBrokersServiceBrokersTeamsAddUsersCreate,
  useBrokersServiceBrokersTeamsCreate,
  useBrokersServiceBrokersTeamsListKey,
  useBrokersServiceBrokersTeamsPartialUpdate,
  useBrokersServiceBrokersTeamsRemoveUserCreate,
  useBrokersServiceBrokersTeamsRetrieveKey
} from '@insurance/api/queries'
import { BrokerTeam } from '@insurance/api/requests'
import { BrokerUserSelect } from '@insurance/components'
import { useValidationsField } from '@insurance/hooks'
import {
  PermissionsCountBadge,
  PermissionsPicker,
  PermissionsTabLabel,
  PermissionsTotalText
} from '@shared/components'
import {
  getFullName,
  getPermissionsSummary,
  showApiErrors
} from '@shared/utils'
import { useQueryClient } from '@tanstack/react-query'
import { App, Button, Flex, Form, Input, Modal, Tabs } from 'antd'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { CommonPropsTeam } from './types'
import { useBrokerPermissionSections } from './useBrokerPermissionSections'

const DETAILS_TAB = 'details'
const PERMISSIONS_TAB = 'permissions'

export const EditAddTeam = ({
  data,
  broker,
  onSuccess,
  ...props
}: CommonPropsTeam) => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const { message } = App.useApp()
  const [form] = Form.useForm()
  const { required } = useValidationsField()

  const [activeTab, setActiveTab] = useState(DETAILS_TAB)

  const { sections, isLoading: isCatalogLoading } =
    useBrokerPermissionSections(broker)

  const permissions = Form.useWatch('permissions', form) as string[] | undefined

  const summary = useMemo(
    () => getPermissionsSummary(sections, permissions),
    [sections, permissions]
  )

  useEffect(() => {
    if (!data) return form.resetFields()

    form.setFieldsValue({
      ...data,
      permissions: data.permissions?.map(({ permission }) => permission),
      __users: data.users.map(({ id, ...user }) => ({
        value: String(id),
        label: getFullName(user)
      }))
    })
  }, [data, form, props.open])

  const onSuccessRequest = () => {
    queryClient.invalidateQueries({
      queryKey: [useBrokersServiceBrokersTeamsListKey]
    })
    queryClient.invalidateQueries({
      queryKey: [useBrokersServiceBrokersTeamsRetrieveKey, { id: data?.id }]
    })

    message.success(data ? t('edited_successfully') : t('added_successfully'))

    onSuccess()
  }

  const { mutateAsync: addUserMutate, isPending: isAddUserPending } =
    useBrokersServiceBrokersTeamsAddUsersCreate({
      onError: (error) => showApiErrors(error)
    })

  const { mutateAsync: removeUserMutate, isPending: isRemoveUserPending } =
    useBrokersServiceBrokersTeamsRemoveUserCreate({
      onError: (error) => showApiErrors(error)
    })

  const { mutateAsync: mutateEdit, isPending: isEditPending } =
    useBrokersServiceBrokersTeamsPartialUpdate({
      onError: (error) => showApiErrors(error, form)
    })

  const { mutateAsync: mutateCreate, isPending: isCreatePending } =
    useBrokersServiceBrokersTeamsCreate({
      onError: (error) => showApiErrors(error, form)
    })

  const onSubmit = async () => {
    const values = await form.validateFields().catch(() => {
      setActiveTab(DETAILS_TAB)

      return null
    })

    if (!values) return

    const dataValues: BrokerTeam = {
      ...values,
      permissions: values?.permissions?.map((permission: string) => ({
        permission
      })),
      broker,
      __users: undefined
    }

    const addedUsers = values.__users
      ?.map(({ value }: { value: string }) => Number(value))
      ?.filter(
        (value: number) => !data?.users?.some(({ id }) => Number(id) === value)
      )

    const deletedUsers = data?.users
      ?.map(({ id }) => Number(id))
      ?.filter(
        (id) =>
          !values.__users?.some(
            ({ value }: { value: string }) => Number(value) === id
          )
      )

    let dataId = data?.id

    if (data?.id) {
      await mutateEdit({ id: data.id, requestBody: dataValues })
    } else {
      const data = await mutateCreate({ requestBody: dataValues })
      dataId = data?.id
    }

    if (addedUsers?.length && dataId)
      await addUserMutate({ id: dataId, requestBody: { users: addedUsers } })

    if (deletedUsers?.length && dataId)
      await removeUserMutate({
        id: dataId,
        requestBody: { users: deletedUsers }
      })

    onSuccessRequest()
  }

  const isPending =
    isEditPending || isCreatePending || isAddUserPending || isRemoveUserPending

  return (
    <Modal
      title={
        <Flex gap={12} align="center" wrap>
          {data ? t('edit_team') : t('add_team')}

          <PermissionsCountBadge
            active={summary.active}
            total={summary.total}
          />
        </Flex>
      }
      width={900}
      footer={
        <Flex
          align="center"
          justify="space-between"
          gap={12}
          wrap
          className="w-full"
        >
          <PermissionsTotalText active={summary.active} />

          <Flex gap={8}>
            <Button onClick={props.onCancel}>{t('cancel')}</Button>

            <Button type="primary" loading={isPending} onClick={onSubmit}>
              {t('save')}
            </Button>
          </Flex>
        </Flex>
      }
      {...props}
    >
      <Form layout="vertical" form={form}>
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={[
            {
              key: DETAILS_TAB,
              label: t('permissions.tab_team_details'),
              forceRender: true,
              children: (
                <>
                  <Form.Item name="name" label={t('name')} rules={[required]}>
                    <Input placeholder={t('name')} />
                  </Form.Item>

                  <Form.Item name="__users" label={t('agents')}>
                    <BrokerUserSelect
                      mode="multiple"
                      className="w-full"
                      broker={String(broker)}
                      labelInValue
                    />
                  </Form.Item>
                </>
              )
            },
            {
              key: PERMISSIONS_TAB,
              label: <PermissionsTabLabel active={summary.active} />,
              forceRender: true,
              children: (
                <Form.Item name="permissions" noStyle>
                  <PermissionsPicker
                    sections={sections}
                    isLoading={isCatalogLoading}
                  />
                </Form.Item>
              )
            }
          ]}
        />
      </Form>
    </Modal>
  )
}
